import { Col, Edit, Form, Input, Row, Select, Switch, useForm, useSelect } from '@pankod/refine-antd';
import { IResourceComponentsProps, useShow } from '@pankod/refine-core';
import { useEffect, useState } from 'react';
import { ILabel } from 'types/label.interface';
import { IPackage } from 'types/package.interface';
import { IUser } from 'types/user.interface';

export const SubscriptionPackageEdit: React.FC<IResourceComponentsProps> = () => {
    const { formProps, saveButtonProps, formLoading } = useForm<IPackage>();

    const { queryResult } = useShow<IPackage>();
    const { data, isLoading } = queryResult;
    const record: any = data?.data;

    const [isShowListUser, setShowListUser] = useState<boolean>(record?.data?.isLimitAccess);

    useEffect(() => {
        setShowListUser(record?.data.isLimitAccess);
    }, [record?.data.isLimitAccess]);

    const { selectProps: LabelSelectProps } = useSelect<ILabel>({
        resource: 'labels',
        fetchSize: 1000,
        optionLabel: 'name',
        optionValue: '_id',
    });

    const { selectProps: UserSelectProps } = useSelect<IUser>({
        resource: 'users',
        fetchSize: 10000,
        optionLabel: 'email',
        optionValue: '_id',
        onSearch: (value) => {
            return [];
        },
    });

    return (
        <Edit saveButtonProps={saveButtonProps} isLoading={isLoading || formLoading}>
            <Form {...formProps} layout='vertical'>
                <Row gutter={24}>
                    <Col span={16}>
                        <Form.Item
                            label='Name'
                            name='name'
                            rules={[
                                {
                                    required: true,
                                    message: 'Name is required',
                                },
                            ]}
                            initialValue={record?.data.name}
                        >
                            <Input defaultValue={record?.data.name} />
                        </Form.Item>
                    </Col>
                    <Col span={8}>
                        <Form.Item
                            label='Is Active'
                            name='isActive'
                            rules={[
                                {
                                    required: false,
                                },
                            ]}
                        >
                            <Switch defaultChecked={record?.data?.isActive} />
                        </Form.Item>
                    </Col>
                </Row>
                <Row gutter={24}>
                    <Col span={8}>
                        <Form.Item
                            label='Is Limit Access'
                            name='isLimitAccess'
                            rules={[
                                {
                                    required: false,
                                },
                            ]}
                        >
                            <Switch onClick={(value) => setShowListUser(value)} defaultChecked={record?.data?.isLimitAccess} />
                        </Form.Item>
                    </Col>
                    {isShowListUser && (
                        <Col span={16}>
                            <Form.Item
                                label='Only Users Can Access'
                                name='userAccess'
                                rules={[
                                    {
                                        required: false,
                                    },
                                ]}
                                initialValue={record?.data?.userAccess?.map((user: any) => user?._id)}
                            >
                                <Select
                                    defaultValue={record?.data?.userAccess?.map((user: any) => user?._id)}
                                    mode='multiple'
                                    {...UserSelectProps}
                                    filterOption={(input, option) => (option?.label as string)?.toLowerCase().includes(input.toLowerCase())}
                                />
                            </Form.Item>
                        </Col>
                    )}
                </Row>
                <Row gutter={24}>
                    <Col span={8}>
                        <Form.Item
                            label='Days Recurring'
                            name='recurringDays'
                            initialValue={record?.data?.recurringDays}
                            rules={[
                                {
                                    required: true,
                                    message: 'Days Recurring is required',
                                },
                            ]}
                        >
                            <Input disabled type='number' defaultValue={record?.data?.recurringDays} />
                        </Form.Item>
                    </Col>
                    <Col span={8}>
                        <Form.Item
                            label='Max Downloads Per Month'
                            name='maxDownloadsPerMonth'
                            rules={[
                                {
                                    required: true,
                                    message: 'Max Downloads Per Month is required',
                                },
                            ]}
                            initialValue={record?.data?.maxDownloadsPerMonth}
                        >
                            <Input type='number' defaultValue={record?.data?.maxDownloadsPerMonth} />
                        </Form.Item>
                    </Col>
                    <Col span={8}>
                        <Form.Item
                            label='Price'
                            name='price'
                            rules={[
                                {
                                    required: true,
                                    message: 'Price is required',
                                },
                            ]}
                            initialValue={record?.data.price}
                        >
                            <Input defaultValue={record?.data.price} type='number' />
                        </Form.Item>
                    </Col>
                </Row>
                <Row gutter={24}>
                    <Col span={16}>
                        <Form.Item
                            label='Labels Included'
                            name='labelsIncluded'
                            rules={[
                                {
                                    required: true,
                                    message: 'Labels Included is required',
                                },
                            ]}
                            initialValue={record?.data?.labelsIncluded?.map((label: any) => label._id)}
                        >
                            <Select mode='multiple' {...LabelSelectProps} defaultValue={record?.data?.labelsIncluded?.map((label: any) => label._id)} />
                        </Form.Item>
                    </Col>
                    {record?.data?.planId && (
                        <Col span={8}>
                            <Form.Item label='Subscription Plan ID'>
                                <Input disabled defaultValue={record?.data?.planId} />
                            </Form.Item>
                        </Col>
                    )}
                </Row>
                <Form.Item
                    label='Description'
                    name='description'
                    rules={[
                        {
                            required: false,
                        },
                    ]}
                    initialValue={record?.data.description}
                >
                    <textarea
                        style={{
                            width: '100%',
                            color: '#626262',
                            border: '1px solid #d9d9d9',
                            padding: '4px 12px',
                            borderRadius: '6px',
                            fontSize: '14px',
                        }}
                        defaultValue={record?.data.description}
                    />
                </Form.Item>
            </Form>
            <div style={{ color: '#626262', fontSize: 14, fontWeight: 700 }}>Active Subscribers</div>
            {record?.data?.userSubscriptionPackage.length > 0 && record?.data?.userSubscriptionPackage.filter((item: any) => !item.isCancel)?.map((data: any) => data?.user.email).length > 0 ? (
                <Select mode='multiple' value={record?.data?.userSubscriptionPackage.filter((item: any) => !item.isCancel)?.map((data: any) => data?.user.email)} />
            ) : (
                0
            )}
            <div style={{ color: '#626262', fontSize: 14, fontWeight: 700, marginTop: '20px' }}>Cancelled Subscribers</div>
            {record?.data?.userSubscriptionPackage.length > 0 && record?.data?.userSubscriptionPackage.filter((item: any) => item.isCancel)?.map((data: any) => data?.user.email).length > 0 ? (
                <Select mode='multiple' value={record?.data?.userSubscriptionPackage.filter((item: any) => item.isCancel)?.map((data: any) => data?.user.email)} />
            ) : (
                0
            )}
        </Edit>
    );
};
